/**
 * wallet.store.ts — Zustand store for wallet state
 *
 * Manages: accounts, balances, resources, active session.
 * Follows Unicove pattern: hash-based change detection to avoid redundant renders.
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { Session } from '@wharfkit/session'
import { accountManager } from '@properwallet/wallet-core'
import type { StoredAccount } from '@properwallet/wallet-core'
import {
  loadAllTokenBalances,
  loadAccountResources,
  loadAccountNfts,
  fetchTokenPrices,
  setAvatarOnChain,
  clearAvatarOnChain,
  invalidateAvatarCache,
  resolveAvatar,
  transferNft,
  validateNftTransfer,
  clearPersistedWharfSession,
  persistWharfSession,
  type TokenBalance,
  type AccountResources,
  type NftAsset,
} from '@properwallet/chain-eos'
import { createSessionFromPrivateKey } from '../lib/createSessionFromPrivateKey'

// ─── Session token helpers ────────────────────────────────────────────────────
// sessionStorage is tab-scoped and cleared when the browser tab closes.
// After PIN unlock we store the private key here so that page reloads within
// the same browser session (e.g. Vite HMR restart) skip the PIN prompt.
// The key is never written to localStorage or any persistent storage by us.

const SESSION_TOKEN_KEY = 'pw_session_token'
const SESSION_TOKEN_TTL_MS = 8 * 60 * 60 * 1000 // 8 hours

interface SessionToken {
  accountName: string
  privateKey: string
  storedAt: number
}

function saveSessionToken(accountName: string, privateKey: string): void {
  try {
    const token: SessionToken = { accountName, privateKey, storedAt: Date.now() }
    sessionStorage.setItem(SESSION_TOKEN_KEY, JSON.stringify(token))
  } catch {
    // sessionStorage may be unavailable (e.g. in some embedded contexts)
  }
}

function loadSessionToken(): SessionToken | null {
  try {
    const raw = sessionStorage.getItem(SESSION_TOKEN_KEY)
    if (!raw) return null
    const token = JSON.parse(raw) as SessionToken
    if (Date.now() - token.storedAt > SESSION_TOKEN_TTL_MS) {
      sessionStorage.removeItem(SESSION_TOKEN_KEY)
      return null
    }
    return token
  } catch {
    return null
  }
}

function clearSessionToken(): void {
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY)
  } catch {
    // ignore
  }
}

// ─── In-memory PIN cache (switch-account support) ─────────────────────────────
// Deliberately a bare module variable, never written to localStorage,
// sessionStorage, or the Zustand store itself (so it never shows up in
// devtools/persist middleware). It only lives for the JS heap lifetime of an
// unlocked session and is wiped on lock/reset — same threat model as the
// plaintext private key we already keep in sessionStorage for 8h above, just
// without the persistence.
//
// Why it exists: this wallet is multi-account, but every account's key is
// encrypted independently and can only be decrypted with the PIN (there is
// no shared secret we can cache instead — see AccountManager.unlockAccountWithPin).
// setActiveAccount() used to switch `activeAccount` without ever rebuilding
// `activeSession`, so the WharfKit session kept signing with the PREVIOUSLY
// unlocked account's key while the transaction's `authorization.actor` named
// the newly selected account — the chain silently rejects that as a missing/
// wrong authority. Caching the PIN lets us rebuild the correct session
// transparently on switch; if we don't have it cached (password-only unlock,
// or the cache was cleared) we fail safe by locking instead of risking a
// wrong-signer transaction (see setActiveAccount below).
let cachedPin: string | null = null

// ─── Avatar reconciliation ──────────────────────────────────────────────────
// Setting an avatar writes local storage first and the chain second (see
// setAvatar() below), but nothing ever ran the other direction: on a browser/
// device that never made that local write itself — a fresh session on
// properwallet-mobile.vercel.app, a reinstall, a different machine — the
// account's avatarUrl in local storage is simply empty forever, even though
// the properavatar contract has a row for it (set from, say, the Telegram
// Mini App). Local storage was never the source of truth, the contract is —
// this just never asked it.
//
// Fire-and-forget by design: unlocking/switching accounts must not wait on a
// network round trip, and if the chain read fails we leave whatever's
// already stored locally untouched (a resolver miss is ambiguous between
// "no avatar" and "network blip" — see resolveAvatar — so we only ever
// overwrite local state on a definite hit, never on a null).
function syncActiveAvatarFromChain(name: string): void {
  void (async () => {
    try {
      const resolved = await resolveAvatar(name)
      if (!resolved) return
      await accountManager.setAccountAvatar(name, resolved.assetId, resolved.mediaUrl)
      // Guard against a fast account switch landing while this was in flight.
      if (useWalletStore.getState().activeAccount?.name === name) {
        useWalletStore.setState({
          accounts: accountManager.getAccounts(),
          activeAccount: accountManager.getActiveAccount(),
        })
      }
    } catch {
      // best effort
    }
  })()
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type WalletScreen =
  | 'onboarding'
  | 'setup-pin'
  | 'pin'
  | 'wallet'
  | 'transfer'
  | 'discovery'
  | 'governance'
  | 'settings'
  | 'accounts'
  | 'resources'
  | 'messages'
  | 'tools'
  | 'create-account'
  | 'create-token'
  | 'keys'
  | 'multisig'

/**
 * Where to land after PIN unlock when the app was opened from a push
 * notification tap (see hooks/usePushNotifications.ts). `from` is the
 * sender for a message notification, so the chat can open on that peer.
 */
export interface PendingPushTarget {
  account: string
  type: 'transfer' | 'message'
  from?: string
}

interface WalletStore {
  // Init state
  initialized: boolean
  hasWallet: boolean

  // Session (in-memory only, never persisted)
  activeSession: Session | null
  isSessionLocked: boolean

  /**
   * Raw WIF private key held in memory after successful unlock.
   * Used for WebDEX auto-auth seed. Cleared on lockSession().
   */
  unlockedPrivateKey: string | null

  /** True after successful PIN or password unlock this session */
  pinVerified: boolean

  /** Legacy wallet password (forgot PIN path) — never persisted */
  pendingLegacyWalletPassword: string | null
  setPendingLegacyWalletPassword: (v: string | null) => void

  // Accounts
  accounts: StoredAccount[]
  activeAccount: StoredAccount | null

  // Balances
  balances: TokenBalance[]
  balancesLoading: boolean
  balancesLastUpdated: number | null

  // Resources
  resources: AccountResources | null
  resourcesLoading: boolean

  // NFTs
  nfts: NftAsset[]
  nftsLoading: boolean

  // Navigation
  currentScreen: WalletScreen
  previousScreen: WalletScreen | null

  /**
   * Token pre-selected for the Transfer screen, as `${contract}::${symbol}`.
   * Set when the user taps "send" on a specific token card. Null = default (A).
   */
  transferTokenKey: string | null
  /** Recipient pre-filled when Transfer is opened from an account chip. */
  transferRecipient: string | null
  /** Conversation to open when the Chat screen mounts (from an account chip). */
  chatPeer: string | null
  setTransferTokenKey: (key: string | null) => void
  setTransferRecipient: (account: string | null) => void
  setChatPeer: (account: string | null) => void

  /** Set when a push notification is tapped; consumed once unlocked. */
  pendingPushTarget: PendingPushTarget | null
  setPendingPushTarget: (target: PendingPushTarget | null) => void
  /** Switches to the target account (if needed) and navigates, then clears it. */
  consumePendingPushTarget: () => Promise<void>

  // Actions
  init: () => Promise<void>
  resolveRouteAfterBoot: () => Promise<void>
  setSession: (session: Session | null) => void
  lockSession: () => void
  setActiveAccount: (name: string) => Promise<void>
  refreshBalances: () => Promise<void>
  refreshResources: () => Promise<void>
  refreshNfts: () => Promise<void>
  setAvatar: (assetId: string, mediaUrl: string) => Promise<void>
  clearAvatar: () => Promise<void>
  /** null = idle. Surfaced in the NFT tab while the chain call is in flight. */
  avatarStatus: { state: 'saving' | 'saved' | 'error'; message?: string } | null
  sendNft: (assetId: string, to: string, memo?: string) => Promise<void>
  /** null = idle. Surfaced by the send modal while the chain call is in flight. */
  nftSendStatus: { state: 'sending' | 'sent' | 'error'; message?: string; txid?: string } | null
  clearNftSendStatus: () => void
  verifyPin: (pin: string) => Promise<boolean>
  setupPin: (pin: string, legacyWalletPassword?: string) => Promise<void>
  clearPin: () => Promise<void>
  unlockWithPin: (pin: string) => Promise<void>
  unlockWithPassword: (accountName: string, password: string) => Promise<void>
  encryptPinForNewAccount: (accountName: string, pin: string) => Promise<void>
  removeAccount: (accountName: string) => Promise<void>
  revealPrivateKeyWithPin: (accountName: string, pin: string) => Promise<string>
  revealPrivateKeyWithPassword: (accountName: string, password: string) => Promise<string>
  resetWallet: () => Promise<void>
  navigate: (screen: WalletScreen) => void
  goBack: () => void
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useWalletStore = create<WalletStore>()(
  subscribeWithSelector((set, get) => ({
    initialized: false,
    hasWallet: false,
    activeSession: null,
    isSessionLocked: true,
    unlockedPrivateKey: null,
    pinVerified: false,
    pendingLegacyWalletPassword: null,
    accounts: [],
    activeAccount: null,
    balances: [],
    balancesLoading: false,
    balancesLastUpdated: null,
    resources: null,
    resourcesLoading: false,
    nfts: [],
    nftsLoading: false,
    currentScreen: 'onboarding',
    previousScreen: null,
    transferTokenKey: null,
    transferRecipient: null,
    chatPeer: null,
    pendingPushTarget: null,

    setTransferTokenKey: (key) => set({ transferTokenKey: key }),
    setTransferRecipient: (transferRecipient) => set({ transferRecipient }),
    setChatPeer: (chatPeer) => set({ chatPeer }),
    setPendingPushTarget: (pendingPushTarget) => set({ pendingPushTarget }),

    consumePendingPushTarget: async () => {
      const { pendingPushTarget, activeAccount, accounts } = get()
      if (!pendingPushTarget) return
      set({ pendingPushTarget: null })
      const exists = accounts.some(a => a.name === pendingPushTarget.account)
      if (!exists) return // account removed from this device since the push fired
      if (activeAccount?.name !== pendingPushTarget.account) {
        await get().setActiveAccount(pendingPushTarget.account)
        // setActiveAccount locks the session if the cached PIN can't rebuild
        // it (e.g. password-only unlock) — don't navigate into a locked screen.
        if (get().isSessionLocked) return
      }
      if (pendingPushTarget.type === 'message') {
        get().setChatPeer(pendingPushTarget.from ?? pendingPushTarget.account)
        get().navigate('messages')
      } else {
        get().navigate('wallet')
      }
    },

    // ── Init ─────────────────────────────────────────────────────────────────

    init: async () => {
      await accountManager.init()
      const accounts = accountManager.getAccounts()
      const activeAccount = accountManager.getActiveAccount()
      const hasWallet = accounts.length > 0
      set({
        initialized: true,
        hasWallet,
        accounts,
        activeAccount,
        currentScreen: 'onboarding',
        isSessionLocked: true,
        pinVerified: false,
        activeSession: null,
      })
    },

    resolveRouteAfterBoot: async () => {
      const hasAccounts = accountManager.hasAccounts()
      const hasPin = await accountManager.hasPin()
      console.log('[boot] hasAccounts:', hasAccounts, 'hasPin:', hasPin)

      if (!hasAccounts) {
        set({ currentScreen: 'onboarding' })
        return
      }
      if (!hasPin) {
        set({ currentScreen: 'setup-pin' })
        return
      }

      // Try auto-restore from sessionStorage (same browser tab, TTL 8h)
      const token = loadSessionToken()
      if (token && token.accountName === accountManager.getActiveAccount()?.name) {
        try {
          console.log('[boot] session token found, auto-restoring for', token.accountName)
          const session = createSessionFromPrivateKey(token.privateKey, token.accountName)
          await persistWharfSession(session)
          set({
            activeSession: session,
            isSessionLocked: false,
            pinVerified: true,
            unlockedPrivateKey: token.privateKey,
            currentScreen: 'wallet',
          })
          syncActiveAvatarFromChain(token.accountName)
          void get().consumePendingPushTarget()
          return
        } catch (e) {
          console.warn('[boot] session token restore failed, showing PIN screen', e)
          clearSessionToken()
        }
      }

      set({ currentScreen: 'pin' })
    },

    setPendingLegacyWalletPassword: (v) => set({ pendingLegacyWalletPassword: v }),

    // ── Session ───────────────────────────────────────────────────────────────

    setSession: (session) => {
      set({ activeSession: session, isSessionLocked: session === null })
    },

    lockSession: () => {
      clearSessionToken()
      cachedPin = null
      set({ activeSession: null, isSessionLocked: true, pinVerified: false, unlockedPrivateKey: null })
    },

    // ── Accounts ──────────────────────────────────────────────────────────────

    setActiveAccount: async (name) => {
      await accountManager.setActiveAccount(name)
      const accounts = accountManager.getAccounts()
      const activeAccount = accountManager.getActiveAccount()
      set({ accounts, activeAccount, balances: [], resources: null, nfts: [], transferTokenKey: null })

      // The WharfKit session is bound to one account's key and does NOT
      // follow `activeAccount` automatically. Rebuild it for the newly
      // selected account so signed actions (avatar, transfers, ...) use the
      // right key — otherwise the session keeps signing as the previous
      // account while the tx names this one as authorizer, and the chain
      // just rejects it as a missing/wrong authority.
      if (cachedPin) {
        try {
          const u = await accountManager.unlockAccountWithPin(name, cachedPin)
          const session = createSessionFromPrivateKey(u.privateKey, u.name)
          await persistWharfSession(session)
          saveSessionToken(u.name, u.privateKey)
          set({
            activeSession: session,
            isSessionLocked: false,
            pinVerified: true,
            unlockedPrivateKey: u.privateKey,
          })
          syncActiveAvatarFromChain(u.name)
        } catch (e) {
          // Wrong/stale cached PIN, or this account has no PIN configured —
          // fail safe rather than risk transacting with the wrong key. The
          // app-level guard (App.tsx) redirects to the PIN screen whenever
          // isSessionLocked is true on a protected screen.
          console.warn('[account] could not rebuild session after switch, locking:', e)
          cachedPin = null
          clearSessionToken()
          set({ activeSession: null, isSessionLocked: true, unlockedPrivateKey: null })
        }
      } else {
        // No PIN cached (e.g. unlocked via legacy password) — same fail-safe:
        // lock rather than keep signing as the old account.
        clearSessionToken()
        set({ activeSession: null, isSessionLocked: true, unlockedPrivateKey: null })
      }

      get().refreshBalances()
    },

    // ── Balances ──────────────────────────────────────────────────────────────

    refreshBalances: async () => {
      const { activeAccount, balancesLoading } = get()
      const accountName = activeAccount?.name?.trim()
      if (!accountName || balancesLoading) return
      set({ balancesLoading: true })
      try {
        const [rawBalances, prices] = await Promise.all([
          loadAllTokenBalances(accountName),
          fetchTokenPrices(),
        ])
        const balances = rawBalances.map(b => ({
          ...b,
          usdValue: prices[b.symbol] !== undefined
            ? b.amountRaw * prices[b.symbol]
            : undefined,
        }))
        set({ balances, balancesLastUpdated: Date.now() })
      } catch (e) {
        console.error('[wallet] refreshBalances error:', e)
      } finally {
        set({ balancesLoading: false })
      }
    },

    // ── Resources ─────────────────────────────────────────────────────────────

    refreshResources: async () => {
      const { activeAccount, resourcesLoading } = get()
      const accountName = activeAccount?.name?.trim()
      if (!accountName) {
        console.warn('[wallet] skip refreshResources: no account')
        return
      }
      if (resourcesLoading) return
      set({ resourcesLoading: true })
      try {
        const resources = await loadAccountResources(accountName)
        set({ resources })
      } catch (e) {
        console.error('[wallet] refreshResources error:', e)
      } finally {
        set({ resourcesLoading: false })
      }
    },

    refreshNfts: async () => {
      const { activeAccount, nftsLoading } = get()
      const accountName = activeAccount?.name?.trim()
      if (!accountName || nftsLoading) return
      set({ nftsLoading: true })
      try {
        const nfts = await loadAccountNfts(accountName)
        set({ nfts })
      } catch (e) {
        console.error('[wallet] refreshNfts error:', e)
        set({ nfts: [] })
      } finally {
        set({ nftsLoading: false })
      }
    },

    avatarStatus: null,

    /**
     * Local first, chain second. The picture changes immediately on this device
     * even if the transaction fails or the user has no resources — the on-chain
     * row is what makes other people see it, not what makes it work here.
     */
    setAvatar: async (assetId, mediaUrl) => {
      const acc = get().activeAccount
      const name = acc?.name?.trim()
      if (!name) return
      await accountManager.setAccountAvatar(name, assetId, mediaUrl)
      set({
        accounts: accountManager.getAccounts(),
        activeAccount: accountManager.getActiveAccount(),
      })

      const session = get().activeSession
      if (!session) {
        set({ avatarStatus: { state: 'error', message: 'locked' } })
        return
      }
      set({ avatarStatus: { state: 'saving' } })
      try {
        await setAvatarOnChain(session, name, assetId)
        invalidateAvatarCache(name)
        set({ avatarStatus: { state: 'saved' } })
      } catch (e) {
        set({
          avatarStatus: {
            state: 'error',
            message: e instanceof Error ? e.message : 'failed',
          },
        })
      }
    },

    /** Removes the row from the contract and refunds its RAM — "forget me". */
    clearAvatar: async () => {
      const name = get().activeAccount?.name?.trim()
      if (!name) return
      await accountManager.setAccountAvatar(name, '', '')
      set({
        accounts: accountManager.getAccounts(),
        activeAccount: accountManager.getActiveAccount(),
      })

      const session = get().activeSession
      if (!session) return
      set({ avatarStatus: { state: 'saving' } })
      try {
        await clearAvatarOnChain(session, name)
        invalidateAvatarCache(name)
        set({ avatarStatus: { state: 'saved' } })
      } catch (e) {
        set({
          avatarStatus: {
            state: 'error',
            message: e instanceof Error ? e.message : 'failed',
          },
        })
      }
    },

    nftSendStatus: null,
    clearNftSendStatus: () => set({ nftSendStatus: null }),

    /**
     * Sends one NFT. Authorized only by the sender — the recipient does not
     * co-sign, and AtomicAssets fronts the RAM for the row landing in their
     * scope out of its own reserve, not the sender's. The sender's own
     * CPU/NET is covered automatically by the Fuel transact plugin if they
     * come up short (same as every other signed action in this wallet), so
     * there is no separate "buy resources first" step here.
     */
    sendNft: async (assetId, to, memo = '') => {
      const acc = get().activeAccount
      const name = acc?.name?.trim()
      if (!name) return
      const session = get().activeSession
      if (!session) {
        set({ nftSendStatus: { state: 'error', message: 'locked' } })
        return
      }
      const validation = validateNftTransfer({ from: name, to, assetId, memo })
      if (validation) {
        set({ nftSendStatus: { state: 'error', message: validation } })
        return
      }
      set({ nftSendStatus: { state: 'sending' } })
      try {
        const result = await transferNft(session, { from: name, to, assetId, memo })
        set({ nftSendStatus: { state: 'sent', txid: result.txid } })
        // The sent asset is gone from this account's inventory now.
        void get().refreshNfts()
      } catch (e) {
        set({
          nftSendStatus: {
            state: 'error',
            message: e instanceof Error ? e.message : 'failed',
          },
        })
      }
    },

    // ── PIN ───────────────────────────────────────────────────────────────────

    verifyPin: async (pin) => accountManager.verifyPin(pin),

    setupPin: async (pin, legacyWalletPassword) => {
      await accountManager.setupPin(pin, legacyWalletPassword)
      set({
        accounts: accountManager.getAccounts(),
        activeAccount: accountManager.getActiveAccount(),
        pendingLegacyWalletPassword: null,
      })
    },

    clearPin: async () => {
      await accountManager.clearPin()
      set({
        accounts: accountManager.getAccounts(),
        activeAccount: accountManager.getActiveAccount(),
      })
    },

    unlockWithPin: async (pin) => {
      const u = await accountManager.unlockWithPin(pin)
      const session = createSessionFromPrivateKey(u.privateKey, u.name)
      await persistWharfSession(session)
      saveSessionToken(u.name, u.privateKey)
      cachedPin = pin
      set({
        activeSession: session,
        isSessionLocked: false,
        pinVerified: true,
        unlockedPrivateKey: u.privateKey,
      })
      syncActiveAvatarFromChain(u.name)
      void get().consumePendingPushTarget()
    },

    unlockWithPassword: async (accountName, password) => {
      const u = await accountManager.unlock(accountName, password)
      const session = createSessionFromPrivateKey(u.privateKey, u.name)
      await persistWharfSession(session)
      saveSessionToken(u.name, u.privateKey)
      set({
        activeSession: session,
        isSessionLocked: false,
        pinVerified: true,
        unlockedPrivateKey: u.privateKey,
      })
      syncActiveAvatarFromChain(u.name)
      void get().consumePendingPushTarget()
    },

    encryptPinForNewAccount: async (accountName, pin) => {
      await accountManager.encryptPinForAccount(accountName, pin)
      set({
        accounts: accountManager.getAccounts(),
        activeAccount: accountManager.getActiveAccount(),
      })
    },

    removeAccount: async (accountName) => {
      clearSessionToken()
      await clearPersistedWharfSession(accountName)
      await accountManager.removeAccount(accountName)
      const accounts = accountManager.getAccounts()
      const activeAccount = accountManager.getActiveAccount()
      set({
        accounts,
        activeAccount,
        balances: [],
        resources: null,
        nfts: [],
        activeSession: null,
        isSessionLocked: true,
        pinVerified: false,
      })
      if (accounts.length === 0) {
        set({ currentScreen: 'onboarding' })
      }
    },

    revealPrivateKeyWithPin: async (accountName, pin) => {
      const u = await accountManager.unlockAccountWithPin(accountName, pin)
      return u.privateKey
    },

    revealPrivateKeyWithPassword: async (accountName, password) => {
      const u = await accountManager.unlock(accountName, password)
      return u.privateKey
    },

    resetWallet: async () => {
      clearSessionToken()
      cachedPin = null
      await accountManager.resetWallet()
      set({
        initialized: false,
        hasWallet: false,
        accounts: [],
        activeAccount: null,
        activeSession: null,
        isSessionLocked: true,
        pinVerified: false,
        unlockedPrivateKey: null,
        balances: [],
        resources: null,
        nfts: [],
        currentScreen: 'onboarding',
        previousScreen: null,
      })
    },

    // ── Navigation ────────────────────────────────────────────────────────────

    navigate: (screen) => {
      const current = get().currentScreen
      set({ previousScreen: current, currentScreen: screen })
    },

    goBack: () => {
      const prev = get().previousScreen
      if (prev) {
        set({ currentScreen: prev, previousScreen: null })
      }
    },
  })),
)
